import React, { useState } from 'react';
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from '../../hooks/useBrandingColors';
import { companiesManagementService } from '../../services/companiesManagementService';
import {
  labelCls, inputBase, tariffLabelCls, TARIFF_MODULES,
  CloseIcon, GiftIcon, QuestionIcon, ChevronUp, ChevronDown, InfoTooltip,
  maskDate, toIso, isDateInvalid, moneyMask,
} from './companyModalUi';
import CustomCheckbox from '../ui/CustomCheckbox';

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

// Тарифы по умолчанию по задаче TASKNEIROKLYUCH-854: Скоринг=1, Диалог=20, AI-скрининг=80
const DEFAULT_TARIFFS = {
  searchCreditsPriceKopecks: '1',
  dialogCreditsPriceKopecks: '20',
  interviewCreditsPriceKopecks: '80',
};

export const CreateCompanyModal: React.FC<Props> = ({ onClose, onCreated }) => {
  const colors = useBrandingColors();

  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [description, setDescription] = useState('');
  const [subscriptionDate, setSubscriptionDate] = useState(''); // «Дата начала подписки» (дд.мм.гггг)
  const [pilotDate, setPilotDate] = useState(''); // дата старта пилота — по клику на подарок
  const [pilotOpen, setPilotOpen] = useState(false);
  const [initialBalanceRub, setInitialBalanceRub] = useState('');
  const [maxAccounts, setMaxAccounts] = useState('18');
  const [tariffs, setTariffs] = useState(DEFAULT_TARIFFS);
  const [relevanterEnabled, setRelevanterEnabled] = useState(true);
  const [atsEnabled, setAtsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !ownerEmail.trim()) return;

    setLoading(true);
    setError(null);
    // Форма принимает рубли (₽/шт) — конвертируем в копейки для API
    const priceKopecks = {
      searchCreditsPriceKopecks: parseInt(tariffs.searchCreditsPriceKopecks, 10) * 100,
      dialogCreditsPriceKopecks: parseInt(tariffs.dialogCreditsPriceKopecks, 10) * 100,
      interviewCreditsPriceKopecks: parseInt(tariffs.interviewCreditsPriceKopecks, 10) * 100,
    };
    try {
      const created = await companiesManagementService.createCompany({
        name: name.trim(),
        ownerEmail: ownerEmail.trim(),
        description: description.trim() || undefined,
        pilotStartDate: toIso(pilotDate),
        subscriptionStartDate: toIso(subscriptionDate),
        // Баланс в копейках (0 если не задан — по требованиям задачи)
        initialBalanceKopecks: initialBalanceRub ? Math.round(parseFloat(initialBalanceRub) * 100) : 0,
        maxAccounts: parseInt(maxAccounts, 10) || 18,
        relevanterEnabled,
        atsEnabled,
        ...priceKopecks,
      });
      // Тарифы читаются из отдельного эндпойнта (GET /pricing) — дублируем запись через
      // PUT /pricing, чтобы цены гарантированно сохранились (как и при редактировании).
      if (created?.id != null) {
        try { await companiesManagementService.setPricing(created.id, priceKopecks); }
        catch { /* не блокируем создание, если pricing-эндпойнт недоступен */ }
      }
      onCreated();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка создания компании');
    } finally {
      setLoading(false);
    }
  };

  const stepTariff = (key: keyof typeof DEFAULT_TARIFFS, delta: number) =>
    setTariffs(prev => {
      const next = Math.max(0, (parseInt(prev[key], 10) || 0) + delta);
      return { ...prev, [key]: String(next) };
    });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-[540px] max-w-[calc(100vw-32px)] max-h-[90vh] overflow-y-auto rounded-[20px] bg-[#F5F6F1]">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6">
          {/* Заголовок: по центру + крестик справа */}
          <div className="flex items-center justify-between h-8">
            <span className="w-8 shrink-0" />
            <h2 className="flex-1 text-center text-[24px] leading-[32px] font-medium tracking-[-0.015em] text-black">
              Создание новой компании
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 shrink-0 flex items-center justify-center text-[#868C98] hover:text-[#0A0D14]"
              aria-label="Закрыть"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="h-px bg-[#EBE7DC]" />

          <div className="flex flex-col gap-6">
            {/* Строка 1: Название + Email */}
            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls}>
                  Название компании <span className="text-[#DF1C41]">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Например: WMT Group"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className={`${inputBase} px-3 text-[14px] leading-[20px]`}
                />
              </div>
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls}>
                  Email владельца (логин) <span className="text-[#DF1C41]">*</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="hr@wmtgroup.ru"
                  value={ownerEmail}
                  onChange={e => setOwnerEmail(e.target.value)}
                  className={`${inputBase} px-3 text-[14px] leading-[20px]`}
                />
              </div>
            </div>

            {/* Описание */}
            <div className="flex flex-col gap-3">
              <label className={labelCls}>Описание деятельности компании</label>
              <textarea
                placeholder="Опишите, чем занимается ваша компания..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                className={`${inputBase} h-[60px] px-3 py-2.5 text-[14px] leading-[20px] resize-none`}
              />
            </div>

            {/* Строка 3: Дата начала подписки + Баланс (с подарком) + Места */}
            <div className="flex gap-3">
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls}>Дата начала подписки</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="дд.мм.гггг"
                  value={subscriptionDate}
                  onChange={e => setSubscriptionDate(maskDate(e.target.value))}
                  className={`${inputBase} px-3 text-[16px] leading-[20px] ${isDateInvalid(subscriptionDate) ? '!border-[#DF1C41]' : ''}`}
                />
              </div>

              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls}>Баланс компании</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setPilotOpen(o => !o)}
                    className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-[4px] hover:bg-[#F8F8F8] transition-colors"
                    title="Дата старта пилота"
                    aria-label="Дата старта пилота"
                  >
                    {/* Активное состояние (пилот задан) — подарок #6E3FF3 по макету */}
                    <GiftIcon color={pilotDate ? '#6E3FF3' : '#ABACA7'} />
                  </button>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={initialBalanceRub}
                    onChange={e => setInitialBalanceRub(moneyMask(e.target.value))}
                    className={`${inputBase} pl-10 pr-7 text-[16px] leading-[20px]`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[16px] leading-[20px] text-[#0A0D14]">₽</span>

                  {pilotOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setPilotOpen(false)} />
                      <div className="absolute z-20 top-[46px] left-0 w-[240px] rounded-[10px] border border-[#E2E4E9] bg-white shadow-lg p-3 flex flex-col gap-2">
                        <label className="text-[14px] leading-[20px] font-medium text-[#0A0D14]">
                          Дата старта пилота
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="дд.мм.гггг"
                          value={pilotDate}
                          onChange={e => setPilotDate(maskDate(e.target.value))}
                          autoFocus
                          className={`${inputBase} px-3 text-[14px] leading-[20px] ${isDateInvalid(pilotDate) ? '!border-[#DF1C41]' : ''}`}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="w-[72px] shrink-0 flex flex-col gap-3">
                <label className={`${labelCls} flex items-center gap-1 whitespace-nowrap`}>
                  Места{' '}
                  <InfoTooltip text="Максимальное количество аккаунтов рекрутеров">
                    <span className="cursor-help">
                      <QuestionIcon />
                    </span>
                  </InfoTooltip>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Места"
                  value={maxAccounts}
                  onChange={e => setMaxAccounts(e.target.value.replace(/[^\d]/g, ''))}
                  className={`${inputBase} px-3 text-[16px] leading-[20px] text-center`}
                />
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <p className={labelCls}>Доступные модули</p>
              <div className="grid grid-cols-2 gap-3 rounded-[12px] border border-[#E2E4E9] bg-white px-4 py-3">
                <CustomCheckbox
                  checked={relevanterEnabled}
                  onChange={setRelevanterEnabled}
                  label="Релевантер"
                />
                <CustomCheckbox
                  checked={atsEnabled}
                  onChange={setAtsEnabled}
                  label="ATS"
                />
              </div>
            </div>

            {/* Настройка тарифов на ии модули — без обёртки/бокса */}
            <div className="flex flex-col gap-3">
              <p className={labelCls}>Настройка тарифов на ии модули</p>
              <div className="flex gap-3">
                {TARIFF_MODULES.map(m => (
                  <div key={m.key} className="flex-1 flex flex-col gap-2">
                    <label className={tariffLabelCls}>{m.label}</label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={tariffs[m.key]}
                        onChange={e => setTariffs(prev => ({ ...prev, [m.key]: e.target.value.replace(/[^\d]/g, '') }))}
                        className={`${inputBase} pl-4 pr-7 text-[16px] leading-[20px]`}
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col">
                        <button type="button" onClick={() => stepTariff(m.key, 1)} aria-label="Увеличить" className="-mb-1 h-3.5 flex items-center">
                          <ChevronUp />
                        </button>
                        <button type="button" onClick={() => stepTariff(m.key, -1)} aria-label="Уменьшить" className="-mt-1 h-3.5 flex items-center">
                          <ChevronDown />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && <p className="text-[14px] text-[#DF1C41]">{error}</p>}
          </div>

          <div className="h-px bg-[#EBE7DC]" />

          {/* Кнопки */}
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-12 rounded-[12px] bg-[#E9EAE4] text-[#7D776C] text-[15px] leading-[28px] font-semibold tracking-[0.01em] hover:bg-[#e0e1da] transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim() || !ownerEmail.trim()}
              className="flex-1 h-12 rounded-[12px] text-white text-[15px] leading-[28px] font-semibold tracking-[0.01em] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={getButtonInlineStyles(colors, 'primary')}
              onMouseEnter={e => { if (!loading) Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary')); }}
              onMouseLeave={e => { if (!loading) Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary')); }}
            >
              {loading ? 'Создание...' : 'Добавить компанию'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
